import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    EventEmitter,
    type EventListener,
    type ToastMessage,
    type ToastMessageInfo,
    type ToastMessageNotification,
    type ToastMessageUpdate,
} from "../../EventEmitter";
import { AgentAvatar, MemberAvatar } from "./Avatar";
import DeadlineTag, { deadlineToCSSColor } from "./DeadlineTag";
import Icon, { PictogramIcon } from "./Icon";
import "./ToastMessage.css";
import { useGet } from "@albertine/api-client";
import type { DeadlineStep, TaskDto } from "@albertine/types";
import { DeadlineStepToTextMap } from "../../common/types/Deadline";
import { formatMessageToString } from "../../common/utils/message.util";
import { AlbertineServiceAgentSenderId } from "../../common/validate";
import routes from "../../utils/routes";

type Toast = { id: number; visible: boolean; toast: ToastMessage };

export const ToastMessageInfoView = function ToastMessageInfoView({
    toast,
    visible = true,
}: {
    toast: ToastMessageInfo;
    visible?: boolean;
}) {
    return (
        <div
            className={classNames("toast-message", "toast-message__info", {
                "toast-message__error": toast.type === "error",
                "animate-in": visible,
                "animate-out": !visible,
            })}
        >
            {toast.type === "error" ? (
                <PictogramIcon.Close.Small.E90 />
            ) : (
                <PictogramIcon.Check.Small.P90 />
            )}
            {toast.message}
        </div>
    );
};

export const ToastMessageNotificationView = function ToastMessageNotificationView({
    toast,
    visible = true,
}: {
    toast: ToastMessageNotification;
    visible?: boolean;
}) {
    const history = useHistory();
    return (
        <div
            className={classNames("toast-message", "toast-message__notification", {
                "animate-in": visible,
                "animate-out": !visible,
            })}
            onClick={() => {
                history.push(toast.link);
            }}
        >
            {toast.agent && <AgentAvatar agentId={toast.agent} size="mid" />}
            {toast.member && <MemberAvatar memberId={toast.member} size="mid" />}
            <div className="toast-message__middle">
                <div className="toast-message__title">{toast.title}</div>
                <div className="toast-message__subtitle">
                    <div className="toast-message__subtitle-truncate">
                        {toast.deadlineAt && (
                            <DeadlineTag deadlineAt={toast.deadlineAt} />
                        )}
                        {toast.subtitle}
                    </div>
                </div>
            </div>
            <Icon.ArrowForward.Medium />
            <div
                className="toast-message__completion-indicator"
                style={{ backgroundColor: toast.color }}
            />
        </div>
    );
};

export const ToastMessageUpdateView = function ToastMessageUpdateView({
    visible = true,
}: {
    toast: ToastMessageUpdate;
    visible?: boolean;
}) {
    return (
        <div
            className={classNames("toast-message", "toast-message__notification", {
                "animate-in": visible,
                "animate-out": !visible,
            })}
        >
            <div className="toast-message__middle">
                <div className="toast-message__title">A new version is available</div>
                <div className="toast-message__subtitle">
                    <div className="toast-message__subtitle-truncate">
                        Reload to get the latest updates.
                    </div>
                </div>
            </div>
            <button
                type="button"
                className="toast-message__action"
                onClick={(event) => {
                    // Stop the click from bubbling to the toast wrapper, which
                    // would dismiss the prompt instead of reloading.
                    event.stopPropagation();
                    window.location.reload();
                }}
            >
                Reload
            </button>
        </div>
    );
};

const ToastMessageItem = function ToastMessageItem(props: { toast: Toast }) {
    const { toast } = props;
    switch (toast.toast.type) {
        case "info":
            return <ToastMessageInfoView toast={toast.toast} visible={toast.visible} />;
        case "error":
            return <ToastMessageInfoView toast={toast.toast} visible={toast.visible} />;

        case "notification":
            return (
                <ToastMessageNotificationView
                    toast={toast.toast}
                    visible={toast.visible}
                />
            );

        case "update":
            return (
                <ToastMessageUpdateView toast={toast.toast} visible={toast.visible} />
            );

        default:
            return null;
    }
};

const itemFixedHeight = (toast: ToastMessage) => {
    switch (toast.type) {
        case "notification":
        case "update":
            return 90;
        default:
            return 60;
    }
};

const toastTimeVisible = (toast: ToastMessage) => {
    // Match these times with animations on ToastMessage.css
    const secondsToMs = 1000;
    switch (toast.type) {
        case "notification":
            return 8 * secondsToMs;
        default:
            return 4 * secondsToMs;
    }
};

const calculateToastTop = (items: Toast[], index: number) =>
    items
        .slice(index + 1)
        .reduce((height, item) => height + itemFixedHeight(item.toast), 0);

const displayedDueRequestIds = {
    dueNow: new Set<string>(),
    dueInExactly30Minutes: new Set<string>(),
    dueInExactly60Minutes: new Set<string>(),
};

type DueRequestForToast = {
    id: string;
    title?: string | null;
    nextDeadlineStep: DeadlineStep | null;
    nextDeadlineAt: string | null;
    assignedToAgentId?: string | null;
};

function displayDueRequestToastsIfNotDisplayed(
    requests: DueRequestForToast[],
    displayedDueRequestIds: Set<string>,
) {
    for (const request of requests) {
        if (displayedDueRequestIds.has(request.id)) {
            continue;
        }
        displayedDueRequestIds.add(request.id);

        const nextDeadlineAt = request.nextDeadlineAt
            ? new Date(request.nextDeadlineAt)
            : undefined;
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                title: request.title ?? "",
                subtitle: request.nextDeadlineStep
                    ? DeadlineStepToTextMap[request.nextDeadlineStep]
                    : "",
                agent: request.assignedToAgentId ?? AlbertineServiceAgentSenderId,
                link: `/request/${request.id}`,
                type: "notification",
                deadlineAt: nextDeadlineAt,
                color: nextDeadlineAt ? deadlineToCSSColor(nextDeadlineAt) : undefined,
            },
        });
    }
}

const displayedDueTaskIds = {
    dueNow: new Set<string>(),
    dueInExactly30Minutes: new Set<string>(),
    dueInExactly60Minutes: new Set<string>(),
};

function displayDueTaskToastsIfNotDisplayed(
    tasks: TaskDto[],
    displayedDueTaskIds: Set<string>,
) {
    for (const task of tasks) {
        if (displayedDueTaskIds.has(task.id)) {
            continue;
        }
        displayedDueTaskIds.add(task.id);

        const mentionDueDate = task.mentionDueDate
            ? new Date(task.mentionDueDate)
            : undefined;
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                title: `Task for member ${task.memberName} due`,
                subtitle: task.message,
                link: routes.chatMessage(
                    task.memberId,
                    task.id,
                    task.requestId ?? undefined,
                ),
                type: "notification",

                deadlineAt: mentionDueDate,
                color: mentionDueDate ? deadlineToCSSColor(mentionDueDate) : undefined,
            },
        });
    }
}

const ToastMessages = function ToastMessages() {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const incomingMessagesAfter = useRef<Date>(new Date());

    const hideOnClick = (toast: Toast) => {
        setToasts((previous) => previous.filter((t) => t.id !== toast.id));
    };

    const { data: incomingMessages } = useGet(
        "incomingMessages",
        { query: { after: incomingMessagesAfter.current.toISOString() } },
        {
            refetchInterval: 5000,
        },
    );
    const { data: dueRequests } = useGet(
        "dueRequests",
        {},
        {
            refetchInterval: 10000,
        },
    );

    const { data: dueTasks } = useGet(
        "dueTasks",
        {},
        {
            refetchInterval: 10000,
        },
    );

    const newestIncomingMessage = incomingMessages?.items[0];

    useEffect(() => {
        if (!newestIncomingMessage) {
            return;
        }
        incomingMessagesAfter.current = new Date(
            Date.parse(newestIncomingMessage.sentAt),
        );

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                title: formatMessageToString(newestIncomingMessage),
                subtitle:
                    newestIncomingMessage.senderName +
                    (newestIncomingMessage.requestTitle
                        ? ` · ${newestIncomingMessage.requestTitle}`
                        : ""),
                member: newestIncomingMessage.memberId,
                link: newestIncomingMessage.requestId
                    ? `/request/${newestIncomingMessage.requestId}`
                    : `/member/${newestIncomingMessage.memberId}`,
                type: "notification",
                color: "var(--a-ds-color-s-30)",
            },
        });
    }, [newestIncomingMessage]);

    useEffect(() => {
        if (!dueRequests) {
            return;
        }

        displayDueRequestToastsIfNotDisplayed(
            dueRequests.dueNow,
            displayedDueRequestIds.dueNow,
        );
        displayDueRequestToastsIfNotDisplayed(
            dueRequests.dueInExactly30Minutes,
            displayedDueRequestIds.dueInExactly30Minutes,
        );
        displayDueRequestToastsIfNotDisplayed(
            dueRequests.dueInExactly60Minutes,
            displayedDueRequestIds.dueInExactly60Minutes,
        );
    }, [dueRequests]);

    useEffect(() => {
        if (!dueTasks) {
            return;
        }

        displayDueTaskToastsIfNotDisplayed(dueTasks.dueNow, displayedDueTaskIds.dueNow);
        displayDueTaskToastsIfNotDisplayed(
            dueTasks.dueInExactly30Minutes,
            displayedDueTaskIds.dueInExactly30Minutes,
        );
        displayDueTaskToastsIfNotDisplayed(
            dueTasks.dueInExactly60Minutes,
            displayedDueTaskIds.dueInExactly60Minutes,
        );
    }, [dueTasks]);

    useEffect(() => {
        const listener: EventListener = {
            name: "showToastMessage",
            listener: (params) => {
                const id = Date.now();
                const toast = params;

                // The update prompt persists until the agent reloads, and only
                // one should ever be on screen, so add it without a dismiss
                // timer and ignore repeat emits while one is already showing.
                if (toast.type === "update") {
                    setToasts((previous) =>
                        previous.some((item) => item.toast.type === "update")
                            ? previous
                            : [...previous, { id, visible: true, toast }],
                    );
                    return;
                }

                const timeVisible = toastTimeVisible(toast);
                const fadeOutTime = 300;
                setToasts((previous) => [
                    ...previous,
                    {
                        id,
                        visible: true,
                        toast,
                    },
                ]);
                setTimeout(() => {
                    setToasts((previous) =>
                        previous.map((item) => {
                            if (item.id === id) {
                                return { ...item, visible: false };
                            }
                            return item;
                        }),
                    );
                }, timeVisible);
                setTimeout(() => {
                    setToasts((previous) => previous.filter((item) => item.id !== id));
                }, timeVisible + fadeOutTime);
            },
        };

        EventEmitter.addListener(listener);

        return () => {
            EventEmitter.removeListener(listener);
        };
    }, []);

    return toasts.length > 0 ? (
        <div className="toast-messages__container">
            {toasts.map((item, index) => (
                <div
                    key={item.id}
                    onClick={() => {
                        hideOnClick(item);
                    }}
                    style={{
                        position: "absolute",
                        top: `${calculateToastTop(toasts, index)}px`,
                        transition: "top 0.3s ease-in-out",
                    }}
                >
                    <ToastMessageItem toast={item} />
                </div>
            ))}
        </div>
    ) : null;
};

export default ToastMessages;
